/**
 * module: SLTSnowAppErrHandler.js
 * author: Rob Blanch
 * created: 3/26/2020
 * usage: Header UI component of Stony Lane Tech Single Page App
 * license: see https://www.stonylanetech.com/licenses/license.txt for details
 * 
 * (C) 2024 Stony Lane Tech LLC
 */
const SLTSnowAppErrCodes = require('./SLTSnowAppErrCodes.js').SLTSnowAppErrCodes;

const SLTSnowAppErrors = [
    {errtype: "SNOW", errcode: 9, errmsg: "SQL command execution error encountered."},
    {errtype: "PROC", errcode: 10000, errmsg: "Incorrect account identifier or password supplied to access sltorange user account."},
    {errtype: "APPERR", errcode: 100001, errmsg: "Missing parameter in URL to provide sltorange user interface experience."},
    {errtype: "PROC", errcode: 30072, errmsg: "User does not have permission to update lot information."},
    {errtype: "PROC", errcode: 10146, errmsg: "Session is not valid."},
    {errtype: "PROC", errcode: 30040, errmsg: "User lacks permission."},
    {errtype: "SNOW", errcode: 20064, errmsg: "Passkey is not registered for this user account with the email address."},
    {errtype: "SNOW", errcode: 20065, errmsg: "Passkey is not registered for this user account with the email address."},
];

var apperr_index = null;
const DefaultErrorMessageWhenNotFound = "SLTSnowAppErrHelper::getUserErrMsgFromError(): no error message found";

/**
 * class: SLTSnowAppErrHelper
 * usage: used to help with throwing/providing standard errors in SLT Snow app (not SLT Snow REST API code base)
 */
class SLTSnowAppErrHelper {
    /**
     * getDefaultErrCode
     *  returns the set default error code for any errors thrown by SLT Snow Admin application
     */
    getDefaultErrCode() {
        return SLTSnowAppErrCodes.APPERR_UNKNOWN;
    }

    /**
     * throwAppErrIfResponseErr
     *      routine to throw err exception with app err message if REST API response is
     * an SLT Service formated error response.
     */
    throwAppErrIfResponseErr(response) {
        if (response && response.errorcode && response.errormessage) {
            this.throwAppErr(response.errorcode, response.errormessage, response.params);
        }
    }

    /**
     * throwAppErr
     *  used to throw an SLTSnow application error
     */
    throwAppErr(errCode = null, errMsg = null, params = null) {
        errCode = (errCode) ? errCode : this.getDefaultErrCode();
        errMsg = (errMsg) ? errMsg : this.getErrMsg(errCode, params);
        throw new Error(errMsg);
    }

    /**
     * getErrMsg
     *  return an SLTSnow application error message for the provided error code
     */
    getErrMsg(errCode, params = null) {
        var errMsg = "SLT Snow App-Err [" + errCode + "]: ";

        if (params) {
            var paramkeys = Object.keys(params);
            for (var idx = 0; idx < paramkeys.length; idx++) {
                if (idx === 0) {
                    errMsg += "(";
                } else {
                    errMsg = errMsg + ", ";
                }

                errMsg = errMsg + paramkeys[idx] + " = " + params[paramkeys[idx]];
            }
            errMsg = errMsg + ")";
        }

        /**
         * TODO: Integrate an error message lookup facility, but for now just return the error code while in development
         * 
         * ROB 10/4/2018
         */

        return errMsg;
    }

    /**
     * userPresentableError
     *  routine to return a user presentable error message for the provided error code
     *  if no user presentable error message is found, then a default error message is returned
     */
    userPresentableError(error) {
        var errcode = (error && error.errcode) ? error.errcode : (error && error.errorcode) ? error.errorcode : this.getDefaultErrCode();
        var modErrMsg = this.getErrMsg(errcode, ('params' in error) ? error.params : ('errormessage' in error) ? [error.errormessage] : null);
        var moderror = new Error(modErrMsg);
        var errMsg = this.getUserErrMsgFromError(moderror);

        // remove the error code off end of message to eliminate user confusion.
        const index = errMsg.lastIndexOf('.');
        const result = index === -1 ? errMsg : errMsg.slice(0, index + 1);
        errMsg = result;

        return (errMsg && errMsg.length > 0 && errMsg !== modErrMsg) ? errMsg : null;
    }

    /**
     * getUserErrMsgFromError
     *  called to obtain a user presentable error message from an error object
     */
    getUserErrMsgFromError(error) {
        var message = (error && error.message) ? error.message : (error && error.errormessage) ? error.errormessage: DefaultErrorMessageWhenNotFound;
        var errors_in_message = this.getErrorsInMessage(message);
        var errorsfound = [];

        if (errors_in_message) {
            var msg_type_pattern = /_([A-Z]*)_/;
            var msg_errcode_pattern = /\[([0-9]*)\]/;
            var ranking = null;
            var maxrankingvalue = -1;
            var maxrankingidx = null;

            /**
             * go get information defined for errors and determine one to show based on ranking
             */
            for (var idx = 0; idx < errors_in_message.length; idx++) {
                var msg_type_array = errors_in_message[idx].match(msg_type_pattern);
                var msg_errcode_array = errors_in_message[idx].match(msg_errcode_pattern);

                if (msg_errcode_array === null || msg_errcode_array.length < 1) {
                    msg_errcode_array = errors_in_message[idx].match(/[0-9]*/);
                }
                
                /**
                 * if we have a valid error type and error code, then go get the app error info for it
                 */
                if (msg_type_array && msg_type_array.length > 0 && msg_errcode_array && msg_errcode_array.length > 0) {
                    var msg_type = null;
                    var msg_errcode = null;

                    msg_type = msg_type_array[1].toUpperCase();
                    msg_errcode = msg_errcode_array[1];

                    if (msg_type && msg_errcode) {
                        var app_err_info = this.getAppErrInfo(msg_type, msg_errcode);
                        if (app_err_info !== undefined && app_err_info !== null) {
                            ranking = this.getAppErrInfoRanking(app_err_info);
                            errorsfound.push(app_err_info);
    
                            if (ranking > maxrankingvalue) {
                                maxrankingidx = errorsfound.length-1;
                            }    
                        }
                    }
                } else if (msg_errcode_array && msg_errcode_array.length > 0) {
                    var msg_errcode = msg_errcode_array[0];
                    if (msg_errcode) {
                        var app_err_info = this.getAppErrInfo("SNOW", msg_errcode);
                        if (app_err_info !== undefined && app_err_info !== null) {
                            ranking = this.getAppErrInfoRanking(app_err_info);
                            errorsfound.push(app_err_info);
    
                            if (ranking > maxrankingvalue) {
                                maxrankingidx = errorsfound.length-1;
                            }    
                        }
                    }
                }
            }

            /**
             * select error message to show to user based on ranking index of greatest value
             */
            if (maxrankingidx >= 0 && maxrankingidx < errorsfound.length) {
                message = errorsfound[maxrankingidx].errmsg + " [" + errorsfound[maxrankingidx].errtype + ": " + errorsfound[maxrankingidx].errcode.toString() + "]";
            } else {
                message = "SLTSnowAppErrHelper::getUserErrMsgFromError(): no ranked error message found for errors";
            }
        }

        return message;
    }

    /**
     * getErrorsInMessage
     *  use regex expressions to find valid error codes from components in an error string returned from service(s)
     */
    getErrorsInMessage(errorstring) {
        var slterr_tag_pattern = /(SLT_[A-Z]*_ERR \[[0-9]*\])/g;
        var slterrors = (errorstring !== undefined) ? errorstring.match(slterr_tag_pattern) : null;
        var sltSnowerr_tag_pattern = /(SLTSNW_[A-Z]*_[A-Z]* \[[0-9]*\])/g;
        var sltSnowerrors = (errorstring !== undefined) ? errorstring.match(sltSnowerr_tag_pattern) : null;
        var sltSnowerr_general_pattern = /\[[0-9]*\]:/g;
        var sltSnowerr_general = (errorstring !== undefined) ? errorstring.match(sltSnowerr_general_pattern) : null;
        var reterrors = null;

        if (slterrors && sltSnowerrors) {
            reterrors = slterrors.concat(sltSnowerrors);
        } else if (slterrors) {
            reterrors = slterrors;
        } else if (sltSnowerrors) {
            reterrors = sltSnowerrors;
        } else if (sltSnowerr_general) {
            /** loop through and remove '[' and ']:' prefix and postfix from string of each errorcode **/
            for (var idx=0; idx < sltSnowerr_general.length; idx++) {
                var errcodestr = sltSnowerr_general[idx];
                if (errcodestr && errcodestr.length >= 4) {
                    var starterrcode = 1;
                    var enderrcode = errcodestr.length - 2;
                    var errcode = errcodestr.substring(starterrcode, enderrcode);
    
                    if (reterrors) {
                        reterrors.push(errcode);
                    } else {
                        reterrors = [ errcode ];
                    }    
                }
            }
        }

        return reterrors;
    }

    /**
     * getAppErrInfo
     *      routine to return the app error info record provided type (ie. JS, SLT, etc.) and error code (ie. 10000, etc.)
     */
    getAppErrInfo(type, errcode) {
        var lookup_hash = type + errcode.toString();

        if (apperr_index === null) {
            apperr_index = new Array(SLTSnowAppErrors.length);
            for (var idx = 0; idx < SLTSnowAppErrors.length; idx++) {
                var apperr_hash = SLTSnowAppErrors[idx].errtype + SLTSnowAppErrors[idx].errcode.toString();
                apperr_index[apperr_hash] = SLTSnowAppErrors[idx];
            }
        }

        return (apperr_index) ? apperr_index[lookup_hash] : null;
    }

    /**
     * getAppErrInfoRanking
     *      return a unique ranking to help figure out which error if set of errors encountered should be presented
     * to user. null returned if error ranking can not be determined.
     */
    getAppErrInfoRanking(errinfo) {
        const rank_reserve_per_type = 500000;
        var ranking = null;
        var rank_type = null;

        if (errinfo) {
            switch (errinfo.errtype) {
                case "Snow":
                    rank_type = 10;
                    break;

                case "PROC":
                    rank_type = 5;
                    break;

                case "APPERR":
                    rank_type = 20;
                    break;
                
                default:
                    rank_type = 0;
            }

            /** TODO - ROB - 10/5/2018: this is overly simplistic and will need a revisit **/
            ranking = (rank_type * rank_reserve_per_type) + errinfo.errcode;
        }

        return ranking;
    }
}

export default SLTSnowAppErrHelper;